import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save, ChevronDown } from "lucide-react";
import { api } from "@/pages/Admin";
import { ImageUpload } from "@/components/admin/ImageUpload";

const emptyArtist = { name: "", genre: "", bio: "", photo_url: "" };
const inputCls = "w-full bg-[#08080c] border border-white/15 px-3 py-2.5 text-sm text-white placeholder:text-slate-600 focus:border-[#00f0ff]";

const ArtistForm = ({ value, onChange, onSubmit, submitLabel, testPrefix }) => (
  <form onSubmit={onSubmit} className="space-y-4">
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div>
        <label className="block font-mono-tech text-[10px] tracking-[0.2em] uppercase text-slate-500 mb-1.5">Artist name</label>
        <input data-testid={`${testPrefix}-name`} required value={value.name} onChange={(e) => onChange({ ...value, name: e.target.value })} className={inputCls} />
      </div>
      <div>
        <label className="block font-mono-tech text-[10px] tracking-[0.2em] uppercase text-slate-500 mb-1.5">Genre</label>
        <input data-testid={`${testPrefix}-genre`} required value={value.genre} onChange={(e) => onChange({ ...value, genre: e.target.value })} className={inputCls} />
      </div>
    </div>
    <div>
      <label className="block font-mono-tech text-[10px] tracking-[0.2em] uppercase text-slate-500 mb-1.5">Bio</label>
      <textarea data-testid={`${testPrefix}-bio`} required rows={3} value={value.bio} onChange={(e) => onChange({ ...value, bio: e.target.value })} className={inputCls} />
    </div>
    <div>
      <label className="block font-mono-tech text-[10px] tracking-[0.2em] uppercase text-slate-500 mb-1.5">Photo</label>
      <ImageUpload value={value.photo_url} onChange={(v) => onChange({ ...value, photo_url: v })} testPrefix={`${testPrefix}-photo`} />
    </div>
    <button data-testid={`${testPrefix}-submit`} type="submit" className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-[#00f0ff] text-black font-mono-tech text-xs tracking-[0.2em] uppercase font-bold hover:bg-white transition-colors">
      <Save size={13} /> {submitLabel}
    </button>
  </form>
);

export const ArtistsManager = () => {
  const [artists, setArtists] = useState([]);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState(emptyArtist);
  const [editing, setEditing] = useState(null);
  const [editDraft, setEditDraft] = useState(emptyArtist);

  const load = () => api.get("/artists").then((r) => setArtists(r.data));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/artists", draft);
      toast.success("Artist added");
      setDraft(emptyArtist);
      setAdding(false);
      load();
    } catch { toast.error("Could not add artist"); }
  };

  const update = async (e) => {
    e.preventDefault();
    try {
      const { id, created_at, ...body } = editDraft;
      await api.put(`/artists/${editing}`, body);
      toast.success("Artist updated");
      setEditing(null);
      load();
    } catch { toast.error("Could not update artist"); }
  };

  const remove = async (a) => {
    if (!window.confirm(`Remove ${a.name} from the roster?`)) return;
    try {
      await api.delete(`/artists/${a.id}`);
      toast.success("Artist removed");
      load();
    } catch { toast.error("Could not remove artist"); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-lg font-bold text-white uppercase">Roster ({artists.length})</h2>
        <button data-testid="admin-add-artist-toggle" onClick={() => setAdding(!adding)} className="flex items-center gap-2 px-5 py-2.5 bg-[#ff0055] text-white font-mono-tech text-[11px] tracking-[0.2em] uppercase font-bold hover:bg-[#00f0ff] hover:text-black transition-colors">
          <Plus size={13} /> New Artist
        </button>
      </div>

      {adding && (
        <div className="bg-[#0c0c12] border border-[#ff0055]/40 p-6">
          <ArtistForm value={draft} onChange={setDraft} onSubmit={create} submitLabel="Add to Roster" testPrefix="admin-new-artist" />
        </div>
      )}

      <div className="space-y-px">
        {artists.map((a) => (
          <div key={a.id} className="bg-[#0c0c12] border border-white/10">
            <div className="flex items-center gap-4 p-4">
              {a.photo_url ? <img src={a.photo_url} alt="" className="w-12 h-12 object-cover" /> : <div className="w-12 h-12 bg-[#101018]" />}
              <div className="flex-1 min-w-0">
                <p className="font-display text-sm font-bold text-white uppercase truncate">{a.name}</p>
                <p className="font-mono-tech text-[10px] tracking-[0.2em] text-slate-500 uppercase">{a.genre}</p>
              </div>
              <button
                data-testid={`admin-edit-artist-${a.name.replace(/\s+/g, "-")}`}
                onClick={() => { setEditing(editing === a.id ? null : a.id); setEditDraft(a); }}
                className="flex items-center gap-1.5 font-mono-tech text-[10px] tracking-[0.2em] uppercase text-[#00f0ff] hover:text-white transition-colors"
              >
                Edit <ChevronDown size={12} className={editing === a.id ? "rotate-180" : ""} />
              </button>
              <button data-testid={`admin-delete-artist-${a.name.replace(/\s+/g, "-")}`} onClick={() => remove(a)} aria-label="Delete artist" className="text-slate-500 hover:text-[#ff0055] transition-colors">
                <Trash2 size={15} />
              </button>
            </div>
            {editing === a.id && (
              <div className="border-t border-white/10 p-6">
                <ArtistForm value={editDraft} onChange={setEditDraft} onSubmit={update} submitLabel="Save Changes" testPrefix="admin-edit-artist" />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
